/**
 * BUG-1862 — the one text rule for a whole-units amount box, shared by admin's
 * `NumberInput` and bidder's `BidAmountInput`: a typed or pasted fraction is
 * refused, never folded into the whole number (¥7,500.5 must not become 75,005).
 */

/**
 * The text a whole-units box keeps after an edit, or `null` when the edit must
 * be refused.
 *
 * The box keeps one visible "." while typing. That is what makes the next digit
 * recognisable as a fraction digit: drop the point on its own keystroke and the
 * following "5" lands on the whole number instead (the original defect, and why
 * truncating at the "." per keystroke does not fix it). A fraction that is all
 * zeros changes nothing, so it is accepted and the zeros dropped ("1,200.00" →
 * "1200."); any other fraction is refused outright.
 *
 * A fraction the box ALREADY held is not new input: a seeded or stepper-written
 * "1.20" can still be edited, and backspaced down to whole — the edit is
 * refused only when it would add fraction digits the box did not have.
 *
 * Separators and stray characters are stripped, and a leading zero ahead of
 * another digit is dropped ("05" → "5"), as the box always did.
 *
 * @param raw - The box's text after the edit, as the browser reports it.
 * @param previous - The box's text before the edit (defaults to empty).
 * @returns The digits to keep (with a trailing "." while one is typed), or null to refuse the edit.
 */
export function wholeUnitsInputText(raw: string, previous = ''): string | null {
    const { whole, fraction } = splitNumeric(raw);
    if (fraction === null) return whole;
    // A bare "." reads as "0." — never as nothing, or a following "5" would
    // land as a whole 5 instead of being refused as a fraction.
    if (!/[1-9]/.test(fraction)) return `${whole || '0'}.`;
    const before = splitNumeric(previous).fraction;
    // Keeping or shortening a fraction that was already there is not new input.
    if (before !== null && /[1-9]/.test(before) && before.startsWith(fraction)) return `${whole}.${fraction}`;
    return null;
}

/**
 * BL-2597 — the text a box that takes the currency's minor units keeps after an
 * edit ("3,500.01" for a per-acre max), or `null` when the edit must be refused.
 * A fraction longer than the currency allows is refused, never truncated or
 * folded into the whole number; with no minor unit (JPY) it is the whole-units
 * rule.
 *
 * @param raw - The box's text after the edit.
 * @param decimalPlaces - The currency's minor-unit digits (`moneyInputProps(currency).decimalPlaces`).
 * @param previous - The box's text before the edit.
 * @returns The digits to keep (with a trailing "." while one is typed), or null to refuse the edit.
 */
export function minorUnitsInputText(raw: string, decimalPlaces: number, previous = ''): string | null {
    if (decimalPlaces === 0) return wholeUnitsInputText(raw, previous);
    const { whole, fraction } = splitNumeric(raw);
    if (fraction === null) return whole;
    return fraction.length <= decimalPlaces ? `${whole || '0'}.${fraction}` : null;
}

/** Digits before and after the first "." (`fraction` null when there is no point). */
function splitNumeric(text: string): { whole: string; fraction: string | null } {
    const cleaned = text.replace(/[^0-9.]/g, '');
    const dot = cleaned.indexOf('.');
    const whole = (dot === -1 ? cleaned : cleaned.slice(0, dot)).replace(/^0+(?=\d)/, '');
    return { whole, fraction: dot === -1 ? null : cleaned.slice(dot + 1).replace(/\./g, '') };
}

/**
 * Group the whole part of a numeric string with thousands commas, leaving any
 * point and fraction as they are ("12345.6" → "12,345.6", "7500." → "7,500.").
 *
 * @param numeric - Digits with at most one ".", as an amount box holds them.
 * @returns The grouped text, or "" for an empty string.
 */
export function groupThousands(numeric: string): string {
    if (!numeric) return '';
    const parts = numeric.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
}
